<template>
  <lf-modal :title="table.title" :close="() => emit('close')">
    <template #header>
      <div class="w-full space-y-8 pb-6">
        <div>
          {{ $t("CHECKOUT.EDIT_TABLE_HELP") }}
        </div>
      </div>
    </template>
    <template #content>
      <div
        class="divide-y divide-gray-200 max-h-[60vh] overflow-y-scroll overflow-x-visible border-t border-gray-200"
        data-cy="checkout-data-table-edit-modal"
      >
        <div class="flex justify-between items-center p-2 sm:p-6 sm:py-4">
          <div class="text-headline">
            {{ $t("COMMON.COLUMN_NAME") }}
          </div>
          <div class="space-x-4 font-medium">
            <button
              class="text-primary"
              data-cy="select-all-button"
              @click="selectAll"
            >
              {{ $t("COMMON.SELECT_ALL") }}
            </button>
            <button
              class="text-red-500"
              data-cy="deselect-all-button"
              @click="deselectAll"
            >
              {{ $t("COMMON.CLEAR") }}
            </button>
          </div>
        </div>
        <draggable
          :model-value="localTable.fields"
          handle=".drag-handle"
          data-cy="draggable-fields-container"
          @update:model-value="handleSelectedFieldOrderChange"
        >
          <template #item="{ element: block }">
            <div
              class="flex justify-between items-center p-2 sm:p-6 sm:py-4 border-b border-gray-200 group"
              :data-cy="`selected-field-${block.name}`"
            >
              <div class="flex items-center space-x-2">
                <div
                  class="drag-handle cursor-grab"
                  :data-cy="`drag-handle-${block.name}`"
                >
                  <icon-base
                    class="relative -top-[1.5px]"
                    :icon="IconDragHandle"
                    width="6"
                    height="10"
                    viewBox="0 0 12 20"
                  />
                </div>
                <div
                  class="text-headline break-long-words max-w-62"
                  :data-cy="`field-label-${block.name}`"
                >
                  {{ block.label }}
                </div>
                <div class="relative">
                  <button
                    class="cursor-pointer -top-px opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center rounded-sm w-6 h-6"
                    :class="{
                      'text-primary bg-primary/10 opacity-100 ':
                        idOfColumnBeingEdited === block.id,
                    }"
                    :data-cy="`edit-label-button-${block.name}`"
                    @click="toggleIdOfColumnBeingEdited(block.id)"
                  >
                    <icon-base
                      :icon="IconEdit"
                      width="12"
                      height="12"
                      view-box="4 0 12 12"
                    />
                  </button>
                  <div
                    v-if="idOfColumnBeingEdited === block.id"
                    v-on-click-outside="clearIdOfColumnBeingEdited"
                    class="absolute top-8 left-0 min-w-80 w-full z-20"
                    :data-cy="`label-editor-${block.name}`"
                  >
                    <checkout-data-label-editor
                      :label="block.label"
                      @save="handleUpdateFieldLabel"
                    />
                  </div>
                </div>
              </div>
              <div>
                <lf-switch
                  :model-value="fieldIsSelected(block.name)"
                  :name="`field-${block.id}`"
                  :data-cy="`${block.name}-toggle`"
                  @toggle-changed="toggleField(block)"
                />
              </div>
            </div>
          </template>
        </draggable>
        <div
          v-for="field in deselectedFields"
          :key="field.id"
          class="flex justify-between items-center p-2 sm:p-6 sm:py-4"
          :data-cy="`deselected-field-${field.name}`"
        >
          <div
            class="text-headline"
            :data-cy="`deselected-field-label-${field.name}`"
          >
            {{ field.label }}
          </div>
          <div>
            <lf-switch
              :model-value="fieldIsSelected(field.name)"
              :name="`field-${field.name}`"
              :data-cy="`${field.name}-toggle`"
              @toggle-changed="toggleField(field)"
            />
          </div>
        </div>
      </div>
      <div
        class="p-4 border-gray-300 border-t flex justify-end"
        data-cy="modal-footer"
      >
        <modal-buttons data-cy="modal-buttons" @save="save" @cancel="cancel" />
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import draggable from "vuedraggable";

import cloneDeep from "lodash/cloneDeep";
import { storeToRefs } from "pinia";

import {
  checkoutBaseToDedicatedStep,
  fieldDefinitionToField,
  isTable,
} from "@/helpers/checkout";
import { useCheckoutOptionsQuery } from "@/hooks/queries/options";
import { useCheckoutStore } from "@/stores/checkout";

import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";
import CheckoutDataLabelEditor from "@/views/checkout/components/CheckoutDataLabelEditor.vue";

import type { CheckoutDataField, CheckoutDataTable } from "@/models/checkout";

const { table } = defineProps<{
  table: CheckoutDataTable;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { data: options } = useCheckoutOptionsQuery();
const { selectedStep } = storeToRefs(useCheckoutStore());

const dataStep = computed(() =>
  checkoutBaseToDedicatedStep(selectedStep.value, "data")
);

const localTable = reactive<CheckoutDataTable>(cloneDeep(table));
const idOfColumnBeingEdited = ref<CheckoutDataField["id"]>();

const fields = computed<CheckoutDataField[]>(() => {
  const block = options.value?.definition.table_blocks.find(
    (block) => block.id === table.block
  );

  if (!block?.fields?.length) {
    return [];
  }

  return block.fields.map((field) => fieldDefinitionToField(block.id, field));
});

const deselectedFields = computed(() => {
  return fields.value.filter((f) => !fieldIsSelected(f.name));
});

const toggleIdOfColumnBeingEdited = (fieldId: CheckoutDataField["id"]) => {
  if (fieldId === idOfColumnBeingEdited.value) {
    clearIdOfColumnBeingEdited();
    return;
  }

  idOfColumnBeingEdited.value = fieldId;
};

const clearIdOfColumnBeingEdited = () => {
  idOfColumnBeingEdited.value = undefined;
};

const fieldIsSelected = (fieldName: CheckoutDataField["name"]) => {
  return localTable.fields.some((f) => f.name === fieldName);
};

const toggleField = (field: CheckoutDataField) => {
  if (fieldIsSelected(field.name)) {
    localTable.fields = localTable.fields.filter((f) => f.name !== field.name);
  } else {
    localTable.fields.push(field);
  }
};

const handleSelectedFieldOrderChange = (fields: CheckoutDataField[]) => {
  localTable.fields = fields;
};

const handleUpdateFieldLabel = (newLabel: CheckoutDataField["label"]) => {
  const fieldIndex = localTable.fields.findIndex(
    (field) => field.id === idOfColumnBeingEdited.value
  );

  if (fieldIndex === -1) {
    return;
  }

  localTable.fields[fieldIndex].label = newLabel;
  clearIdOfColumnBeingEdited();
};

const selectAll = () => {
  localTable.fields = fields.value;
};

const deselectAll = () => {
  localTable.fields = [];
};

const save = () => {
  if (!dataStep.value) {
    return;
  }

  const currentTable = dataStep.value.items?.find(
    (item: CheckoutDataField | CheckoutDataTable) => item.id === table.id
  );

  if (!currentTable || !isTable(currentTable)) {
    return;
  }

  currentTable.fields = localTable.fields;
  emit("close");
};

const cancel = () => {
  emit("close");
};
</script>
